// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import QtyField, { QtyFieldStyles } from "@conklin/App/Components/Qty/QtyField";
import xUseQty from "@conklin/App/Hooks/xUseQty";
import { mergeWithCss, xMask } from "@conklin/XDL/Utilities";
import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import React, { FC } from "react";
import { connect } from "react-redux";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the qtyOrdered will display in a text field for editing.
     * If false, the qtyOrdered will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onQtyOrderedChange?: (qtyOrdered: number) => void;
    extendedStyles?: CartLineQuantityStyles;
    label?: string;
    // XNG-Changes
    showLabel?: boolean;
    showActionButton?: boolean;
    disabled?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    isLoading: state.data.carts.isLoading,
    cartLoadedTime: state.data.carts.cartLoadedTime,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCartLineContext;

export interface CartLineQuantityStyles {
    editableQuantityTextField?: QtyFieldStyles;
    readOnlyQuantityText?: QtyFieldStyles;
}

export const cartLineQuantityStyles: CartLineQuantityStyles = {
    // editableQuantityTextField: {
    //     labelProps: {
    //         variant: "legend",
    //         css: css`
    //             font-size: 10px;
    //             text-transform: uppercase;
    //             font-weight: 600;
    //         `,
    //     },
    // },
};

const CartLineQuantity: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    label,
    isLoading,
    cartLoadedTime,
    onQtyOrderedChange = () => {},
    extendedStyles,
    // XNG-Change
    showLabel = false,
    showActionButton,
    disabled = false,
}) => {
    // Xng-Changes
    const minimumOrderQty = Number(cartLine.properties.minimumOrderQty);
    const multipleSaleQty = Number(cartLine.properties.multipleSaleQty);

    const localOnQtyOrderedChange = (value: string, onError?: () => void) => {
        const inputQty = parseFloat(value);
        if (Number.isNaN(inputQty)) {
            onError?.();
            return;
        }

        onQtyOrderedChange(inputQty);
    };

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        cartLine.qtyOrdered!.toString(),
        localOnQtyOrderedChange,
        cartLoadedTime,
    );

    const [styles] = React.useState(() => mergeWithCss(cartLineQuantityStyles, extendedStyles));

    const { debouncedHandleChange } = xUseQty({
        multipleSaleQty,
        minQtyState: minimumOrderQty,
        setProductQty: value => {
            const event = { currentTarget: { value } };
            changeHandler(event);
        },
        onQtyOrderedChanged: value => {
            if (cartLine.qtyOrdered !== value) {
                localOnQtyOrderedChange(`${value}`);
            }
        },
    });
    // XNG-Changes
    const xChangeHandler = (event: any) => {
        event.currentTarget.value = Number(xMask.qty(event.currentTarget.value));
        changeHandler(event);
    };
    // XNG-Changes
    const xBlurHandler = (event: any) => {
        const eventValue = event?.currentTarget?.value;
        if (eventValue && Number(xMask.qty(eventValue)) === 0) {
            event.currentTarget.value = cartLine.qtyOrdered;
            changeHandler(event);
            return;
        }
        debouncedHandleChange(value);
    };
    if (editable && !isOutOfStock(cartLine) && !cartLine.isRestricted) {
        return (
            <QtyField
                showLabel={showLabel}
                value={value}
                disabled={
                    !cart.canModifyOrder ||
                    cartLine.isPromotionItem ||
                    cart.type === "Job" ||
                    isLoading[cart.id] ||
                    // XNG-Changes
                    disabled
                }
                // Xng-Changes
                onChange={xChangeHandler}
                onKeyDown={keyDownHandler}
                // Xng-Changes
                onBlur={xBlurHandler}
                onClick={event => event.currentTarget.focus()}
                data-test-selector="cartline_qty"
                // Xng-Changes
                min={Math.max(minimumOrderQty || 1, multipleSaleQty || 1)}
                extendedStyles={styles.editableQuantityTextField}
                showActionButton={showActionButton}
                // showNoPriceAlert={cartLine.quoteRequired}
                // Xng-Changes
                multipleSaleQty={multipleSaleQty}
            />
        );
    }

    // XNG-changes: Using QtyField Component with editable={false} to display the number as text
    return <QtyField editable={false} value={value} extendedStyles={styles.readOnlyQuantityText} />;
};

export default connect(mapStateToProps)(withCartLine(CartLineQuantity));
